import * as React from 'react';
import {
  Box,
  HStack,
  Text,
  Button,
  VStack,
  Icon,
  ButtonGroup,
} from '@chakra-ui/react';
import { RenderFormElements } from '@components/form';
import { EmptyStateBox } from '../ui/empty-state';
import { Toaster } from '@ui';
import { FiDownload, FiEye, FiAlertCircle } from 'react-icons/fi';
import { LuDownload, LuPrinter } from 'react-icons/lu';
import { EnvironmentIframe } from '../ui/iframe';
import type { ViewProps } from './types';
import { TabOpenButton } from './TabOpenButton';

export const View: React.FC<ViewProps> = ({
  url,
  blob,
  pdfTitle = 'Document',
  pageTitle = 'Document Preview',
  details = [],
  height = '480px',
  minHeight,
  width,
  disabled = false,
  showOpenTabButton = false,
  showDownloadButton = true,
  showPrintButton = true,
  showFallbackMessage = true,
  buttonProps,
  className,
  style,
  ...rest
}) => {
  const [pdfUrl, setPdfUrl] = React.useState<string>('');
  const [wrapperUrl, setWrapperUrl] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string>('');
  const [fallbackMethod, setFallbackMethod] = React.useState<
    'iframe' | 'object' | 'embed' | 'download'
  >('iframe');
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const objectRef = React.useRef<HTMLObjectElement>(null);
  const embedRef = React.useRef<HTMLEmbedElement>(null);

  // Handle blob input
  React.useEffect(() => {
    if (blob) {
      try {
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setIsLoading(false);
        return () => {
          URL.revokeObjectURL(url);
        };
      } catch (err) {
        setError('Failed to create PDF URL');
        setIsLoading(false);
      }
    } else if (url) {
      setPdfUrl(url);
      setIsLoading(false);
    } else {
      setPdfUrl('');
      setWrapperUrl('');
      setIsLoading(false);
    }
  }, [blob, url]);

  // Create wrapper URL for string URLs (removes PDF sidebar)
  React.useEffect(() => {
    if (!url || blob) {
      setWrapperUrl('');
      return;
    }

    const styles = `
      html, body {
        margin: 0;
        padding: 0;
        height: 100%;
        width: 100%;
        background: #000;
      }
      iframe {
        width: 100%;
        height: 100%;
        border: 0;
        display: block;
      }
    `;

    const html = `<!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>${pageTitle} - ${pdfTitle}</title>
        <style>${styles}</style>
      </head>
      <body>
        <iframe src="${url}" allowfullscreen title="${pdfTitle}"></iframe>
      </body>
    </html>`;

    const blobUrl = URL.createObjectURL(
      new Blob([html], { type: 'text/html' }),
    );
    setWrapperUrl(blobUrl);

    return () => {
      URL.revokeObjectURL(blobUrl);
    };
  }, [url, pdfTitle, pageTitle, blob]);

  const handleDownload = () => {
    const source = blob || (pdfUrl ? { url: pdfUrl } : null);
    if (!source) return;

    try {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${pdfTitle || 'document'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (pdfUrl) {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${pdfTitle || 'document'}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      Toaster.create({
        type: 'success',
        message: 'Download started',
      });
    } catch (err) {
      Toaster.create({
        type: 'error',
        message: 'Download failed',
        detail: 'Unable to download the PDF file',
      });
    }
  };

  const handlePrint = () => {
    const source = blob || (pdfUrl ? { url: pdfUrl } : null);
    if (!source) return;

    if (blob) {
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } else if (pdfUrl) {
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  };

  const handleIframeError = () => {
    setFallbackMethod('object');
  };

  const handleObjectError = () => {
    setFallbackMethod('embed');
  };

  const handleEmbedError = () => {
    setFallbackMethod('download');
  };

  const renderPDFContent = () => {
    const displayUrl = blob ? pdfUrl : wrapperUrl || pdfUrl;

    switch (fallbackMethod) {
      case 'iframe':
        return (
          <EnvironmentIframe
            ref={iframeRef}
            src={displayUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            onError={handleIframeError}
            onLoad={() => setIsLoading(false)}
            allowFullScreen
            loading="eager"
            title={pdfTitle}
          />
        );

      case 'object':
        return (
          <object
            ref={objectRef}
            data={displayUrl}
            type="application/pdf"
            width="100%"
            height="100%"
            onError={handleObjectError}
            onLoad={() => setIsLoading(false)}
          >
            <Text>PDF cannot be displayed. Please download to view.</Text>
          </object>
        );

      case 'embed':
        return (
          <embed
            ref={embedRef}
            src={displayUrl}
            type="application/pdf"
            width="100%"
            height="100%"
            onError={handleEmbedError}
          />
        );

      case 'download':
        return (
          <VStack gap={4} justify="center" align="center" height="100%">
            <Icon as={FiAlertCircle} boxSize={8} color="orange.500" />
            <Text textAlign="center" color="gray.600">
              PDF cannot be displayed in your browser
            </Text>
            <Button colorPalette="blue" onClick={handleDownload}>
              <FiDownload />
              Download PDF
            </Button>
          </VStack>
        );

      default:
        return null;
    }
  };

  if (error) {
    return (
      <Box
        width={width || '100%'}
        height={height}
        border="1px solid"
        borderColor="red.200"
        borderRadius="md"
        p={4}
      >
        <VStack gap={3} justify="center" align="center" height="100%">
          <Icon as={FiAlertCircle} boxSize={6} color="red.500" />
          <Text color="red.600" textAlign="center">
            {error}
          </Text>
          {showDownloadButton && (
            <Button colorPalette="blue" size="sm" onClick={handleDownload}>
              <FiDownload />
              Download PDF
            </Button>
          )}
        </VStack>
      </Box>
    );
  }

  const hasContent = blob || url;

  return (
    <Box display="flex" flexDirection="column" gap={4} {...rest}>
      {(pageTitle || showOpenTabButton) && (
        <HStack width="100%" justifyContent="space-between">
          {pageTitle && (
            <Text textStyle="heading.card" color="secondary.fg">
              {pageTitle}
            </Text>
          )}
          {showOpenTabButton && url && (
            <TabOpenButton
              url={url}
              pdfTitle={pdfTitle}
              pageTitle={pageTitle}
              details={details}
              disabled={disabled}
              {...buttonProps}
            />
          )}
        </HStack>
      )}

      {details && details.length > 0 && (
        <Box>
          <RenderFormElements
            items={details.map((d) => {
              const valueType = typeof d.value;
              const isPrimitive =
                valueType === 'string' ||
                valueType === 'number' ||
                valueType === 'boolean' ||
                d.value == null;
              return isPrimitive
                ? {
                    label: String(d.label),
                    currentValue: d.value as
                      | string
                      | number
                      | boolean
                      | null
                      | undefined,
                  }
                : { label: String(d.label), children: <>{d.value}</> };
            })}
          />
        </Box>
      )}

      <Box
        width={width || '100%'}
        height={height}
        minHeight={minHeight}
        borderWidth="1px"
        borderRadius="md"
        overflow="hidden"
        bg="bg.canvas"
        position="relative"
        className={className}
        style={style}
      >
        {hasContent ? (
          <>
            {isLoading && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={1}
              >
                <Text color="gray.500">Loading PDF...</Text>
              </Box>
            )}

            {renderPDFContent()}

            {(showDownloadButton || showPrintButton) &&
              fallbackMethod !== 'download' && (
                <ButtonGroup
                  position="absolute"
                  top={2}
                  right={2}
                  zIndex={2}
                  size="sm"
                  textStyle="button"
                  color="secondary.solid"
                  borderColor="secondary.emphasized"
                  alignSelf="flex-start"
                  whiteSpace="nowrap"
                >
                  {showDownloadButton && (
                    <Button
                      onClick={handleDownload}
                      opacity={0.8}
                      _hover={{ opacity: 1 }}
                    >
                      <LuDownload />
                      Download
                    </Button>
                  )}
                  {showPrintButton && (
                    <Button
                      size="sm"
                      colorPalette="blue"
                      variant="solid"
                      onClick={handlePrint}
                    >
                      <LuPrinter />
                      Print
                    </Button>
                  )}
                </ButtonGroup>
              )}

            {showFallbackMessage && fallbackMethod !== 'iframe' && (
              <Box
                position="absolute"
                bottom={2}
                left={2}
                right={2}
                bg="yellow.50"
                border="1px solid"
                borderColor="yellow.200"
                borderRadius="md"
                p={2}
                zIndex={2}
              >
                <HStack gap={2}>
                  <Icon as={FiEye} color="yellow.600" />
                  <Text fontSize="sm" color="yellow.800">
                    Using fallback PDF viewer. For best experience, download the
                    PDF.
                  </Text>
                </HStack>
              </Box>
            )}
          </>
        ) : (
          <EmptyStateBox
            title="No PDF to display"
            description={
              blob ? 'Please upload a PDF file' : 'No PDF to preview.'
            }
          />
        )}
      </Box>
    </Box>
  );
};
